import React, { useState, useRef, useEffect } from 'react';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { classNames } from '@repo/ui/src/utils/classNames';

interface RecipeDescriptionProps {
  description: string;
}

const RecipeDescription = ({ description }: RecipeDescriptionProps) => {
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [expandedHeight, setExpandedHeight] = useState(0);
  const [collapsedHeight, setCollapsedHeight] = useState(0);
  const [isExpandable, setIsExpandable] = useState(false);

  const { t } = useTranslation();
  const contentRef = useRef<HTMLParagraphElement>(null);
  const COLLAPSED_LINES = 3;

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const checkHeight = () => {
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight);
      const collapsedHeight = lineHeight * COLLAPSED_LINES;
      setExpandedHeight(el.scrollHeight);
      setCollapsedHeight(collapsedHeight);
      setIsExpandable(el.scrollHeight > collapsedHeight);
    };

    checkHeight();

    const resizeObserver = new ResizeObserver(() => checkHeight());
    resizeObserver.observe(el);

    return () => resizeObserver.disconnect();
  }, [description]);

  const toggleExpand = () => {
    setShowFullDescription(!showFullDescription);
    setIsAnimating(true);
  };

  return (
    <div className="tw-overflow-hidden tw-relative tw-text-neutral-600">
      <div
        className="tw-overflow-hidden tw-transition-all tw-duration-300 tw-ease-in-out"
        style={{
          maxHeight: showFullDescription ? `${expandedHeight}px` : `${collapsedHeight}px`,
        }}
        onTransitionEnd={() => setIsAnimating(false)}
      >
        <p
          ref={contentRef}
          className={classNames(
            'tw-mb-0 tw-leading-6 tw-text-ellipsis tw-transition-all tw-duration-300',
            isExpandable && !showFullDescription && !isAnimating && 'tw-line-clamp-3',
          )}
        >
          {description}
        </p>
      </div>
      {isExpandable && (
        <button className="tw-font-bold tw-underline" onClick={toggleExpand}>
          {showFullDescription
            ? t('recipeContent.showLess', undefined, { default: 'Show Less' })
            : t('recipeContent.showMore', undefined, { default: 'Show More' })}
        </button>
      )}
    </div>
  );
};

export default RecipeDescription;
